import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  Renderer2,
  QueryList,
  ViewChildren,
  HostListener
} from '@angular/core';
import { LocationDto } from '../../../../../model/continent';
import { NgForOf, NgIf } from '@angular/common';
import { CountryInfoElComponent } from '../../../../elements/country-info-el/country-info-el.component';
import { Icons } from '../../../../../model/icon';
import { TextsComponent } from '../../../../elements/texts/texts.component';
import { ThemeService } from '../../../../../services/theme.service';
import { Pagination, PaginationParams } from '../../../../../model/pagination';
import { ContinentService } from '../../../../../services/continent-service';
import { CountryService } from '../../../../../services/country-service';
import { scrollUp } from '../../../../../functions/scroll-up';
import { RouteService } from '../../../../../services/route-service';
import { LoadingComponent } from '../../../../elements/loading/loading.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-destinations-countries',
  imports: [
    NgForOf,
    CountryInfoElComponent,
    TextsComponent,
    NgIf,
    LoadingComponent,
  ],
  templateUrl: './destinations-countries.component.html',
  standalone: true,
  styleUrls: ['./destinations-countries.component.scss']
})
export class DestinationsCountriesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  constructor(
    public themeService: ThemeService,
    protected continentService: ContinentService,
    protected countryService: CountryService,
    protected routeService: RouteService,
    private renderer: Renderer2
  ) {}

  @ViewChild('scrollTrigger') scrollTrigger!: ElementRef;
  @ViewChild('continentsContainer') continentsContainer!: ElementRef;
  @ViewChildren('continentEl') set continentElements(elements: QueryList<ElementRef>) {
    if (elements) {
      elements.forEach((el: ElementRef) => {
        this.renderer.listen(el.nativeElement, 'touchstart', (e: TouchEvent) => {
          const touch = e.touches[0];
          this.touchStartX = touch.clientX;
          this.touchStartY = touch.clientY;
        });
      });
    }
  }

  private observer!: IntersectionObserver;
  private retryCount = 0;
  private maxRetries = 3;
  private destroy$ = new Subject<void>();
  private continentFilterDestroy$ = new Subject<void>();
  private isScrollLoading: boolean = false;
  private isDragging: boolean = false;
  private isPotentialDrag: boolean = false;
  private startX: number = 0;
  private scrollLeft: number = 0;
  private velocity: number = 0;
  private lastX: number = 0;
  private animationFrameId: number | null = null;
  private inertiaAnimationId: number | null = null;
  private wheelTimeout: any = null;
  private dragStartTime: number = 0;
  private dragThreshold: number = 5;
  private clickPrevented: boolean = false;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private isTouchDragging: boolean = false;
  private mouseMoveListener: any = null;
  private mouseUpListener: any = null;
  private touchMoveListener: any = null;
  private touchEndListener: any = null;

  canScrollLeft: boolean = false;
  canScrollRight: boolean = false;

  @Input() alone: boolean = false;
  @Input() filter: LocationDto = new LocationDto();
  @Input() loadContinent: number | undefined = undefined;
  @Input() border: boolean = false;
  @Input() type: 'classic' | 'adventure' = 'classic';
  @Input() adventureId: number | null = null;
  @Output() firstLoading = new EventEmitter<boolean>();
  @Output() emitContinents = new EventEmitter<string | null>();

  continents: LocationDto[] = [];
  filteredContinents: LocationDto[] = [];
  countries: LocationDto[] = [];
  continent: LocationDto = new LocationDto();
  activeId: number | null = null;

  pagination: Pagination<any> = { page: { number: 0, totalPages: 0, size: 0, totalElements: 0 }, content: [] };

  activeContinent: number | null = null;
  continentId: number | null = null;
  showArrows: boolean = false;

  loadingContinents: boolean = false;
  loadingCountries: boolean = false;
  loadingContinent: boolean = false;

  defaultParams = new PaginationParams({
    sort: 'titleEn:ASC',
    query: '',
    pageSize: 10,
    pageNumber: 0,
  });

  antParams = new PaginationParams({
    sort: '',
    query: '',
    pageSize: 10,
    pageNumber: 0,
    continentId: '9'
  });

  adventuresParams = new PaginationParams({
    sort: 'titleEn:ASC',
    query: '',
    pageSize: 200,
    pageNumber: 0,
  });

  fullData: boolean = false;
  first: boolean = false;

  params: PaginationParams = new PaginationParams(this.defaultParams);

  ngOnInit() {
    this.firstLoading.emit(true);
    if (!this.alone) {
      this.getList('continents');
      this.getList('countries');
    } else {
      if (this.loadContinent != undefined) {
        this.getList('continent', this.loadContinent);
      }
    }
  }

  ngAfterViewInit() {
    this.setupIntersectionObserver();
    setTimeout(() => {
      this.updateScrollIndicators();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filter'] && !changes['filter'].firstChange && this.filter?.id) {
      this.getList('continent', this.filter.id);
      if (this.continents.length > 0) {
        const x = this.findPosition(this.continents, this.filter);
        if (x !== -1) {
          this.setContinentFilter(x - 1, this.filter.id ?? 0);
        }
      }
    }
    if (changes['adventureId']) {
      this.getList('countries');
    }

    if (changes['countries'] && !changes['countries'].firstChange) {
      setTimeout(() => {
        this.reinitObserver();
      }, 100);
    }

    if (changes['continents'] && this.continentsContainer) {
      setTimeout(() => {
        this.updateScrollIndicators();
      }, 100);
    }
  }

  ngOnDestroy() {
    this.cleanupEventListeners();
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.inertiaAnimationId) {
      cancelAnimationFrame(this.inertiaAnimationId);
    }
    if (this.wheelTimeout) {
      clearTimeout(this.wheelTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.continentFilterDestroy$.next();
    this.continentFilterDestroy$.complete();
  }

  private cleanupEventListeners(): void {
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
      this.mouseMoveListener = null;
    }
    if (this.mouseUpListener) {
      this.mouseUpListener();
      this.mouseUpListener = null;
    }
    if (this.touchMoveListener) {
      this.touchMoveListener();
      this.touchMoveListener = null;
    }
    if (this.touchEndListener) {
      this.touchEndListener();
      this.touchEndListener = null;
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(event: MouseEvent): void {
    if (this.isDragging || this.isPotentialDrag) {
      this.onMouseUp();
    }
  }

  @HostListener('document:touchend', ['$event'])
  @HostListener('document:touchcancel', ['$event'])
  onDocumentTouchEnd(event: TouchEvent): void {
    if (this.isTouchDragging || this.isPotentialDrag) {
      this.onTouchEnd();
    }
  }

  isLoading(): boolean {
    if (this.alone) {
      return this.loadingContinent;
    } else {
      return this.loadingContinents || this.loadingCountries || this.loadingContinent || this.isScrollLoading;
    }
  }

  onMouseDown(event: MouseEvent): void {
    const container = this.continentsContainer.nativeElement;
    if (container.scrollWidth <= container.clientWidth) {
      return;
    }

    this.dragStartTime = Date.now();
    this.startX = event.pageX - container.offsetLeft;
    this.scrollLeft = container.scrollLeft;
    this.lastX = event.pageX;
    this.velocity = 0;
    this.clickPrevented = false;
    this.isPotentialDrag = true;
    this.mouseMoveListener = this.renderer.listen('document', 'mousemove', (e) => this.onMouseMove(e));
    this.mouseUpListener = this.renderer.listen('document', 'mouseup', (e) => this.onMouseUp());

    event.preventDefault();
    event.stopPropagation();
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isPotentialDrag && !this.isDragging) return;

    const container = this.continentsContainer.nativeElement;
    const currentX = event.pageX - container.offsetLeft;
    const distanceMoved = Math.abs(currentX - this.startX);
    if (!this.isDragging && distanceMoved > this.dragThreshold) {
      this.isDragging = true;
      this.clickPrevented = true;
      this.renderer.addClass(container, 'dragging');
    }

    if (!this.isDragging) return;

    event.preventDefault();
    const x = event.pageX - container.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    const newScrollLeft = this.scrollLeft - walk;
    this.velocity = (event.pageX - this.lastX);
    this.lastX = event.pageX;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = requestAnimationFrame(() => {
      container.scrollLeft = newScrollLeft;
      this.updateScrollIndicators();
    });
  }

  onMouseUp(): void {
    this.cleanupEventListeners();

    if (this.isDragging) {
      this.isDragging = false;
      const container = this.continentsContainer.nativeElement;
      this.renderer.removeClass(container, 'dragging');

      if (Math.abs(this.velocity) > 1) {
        this.applyInertia(this.velocity * 0.9);
      }
    }

    this.isPotentialDrag = false;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  onTouchStart(event: TouchEvent): void {
    const container = this.continentsContainer.nativeElement;
    if (container.scrollWidth <= container.clientWidth) {
      return;
    }

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.startX = touch.clientX - container.getBoundingClientRect().left;
    this.scrollLeft = container.scrollLeft;
    this.lastX = touch.clientX;
    this.velocity = 0;
    this.clickPrevented = false;
    this.isPotentialDrag = true;
    this.dragStartTime = Date.now();
    this.touchMoveListener = this.renderer.listen('document', 'touchmove', (e) => this.onTouchMove(e));
    this.touchEndListener = this.renderer.listen('document', 'touchend', (e) => this.onTouchEnd());
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isPotentialDrag && !this.isTouchDragging) return;

    const container = this.continentsContainer.nativeElement;
    const touch = event.touches[0];
    const currentX = touch.clientX - container.getBoundingClientRect().left;
    const distanceMovedX = Math.abs(currentX - this.startX);
    const distanceMovedY = Math.abs(touch.clientY - this.touchStartY);
    const mobileDragThreshold = 10;
    if (!this.isTouchDragging && distanceMovedX > mobileDragThreshold && distanceMovedX > distanceMovedY) {
      this.isTouchDragging = true;
      this.clickPrevented = true;
      this.renderer.addClass(container, 'dragging');
      event.preventDefault();
    }

    if (!this.isTouchDragging) return;

    event.preventDefault();
    const x = touch.clientX - container.getBoundingClientRect().left;
    const walk = (x - this.startX) * 1.5;
    const newScrollLeft = this.scrollLeft - walk;
    this.velocity = (touch.clientX - this.lastX);
    this.lastX = touch.clientX;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = requestAnimationFrame(() => {
      container.scrollLeft = newScrollLeft;
      this.updateScrollIndicators();
    });
  }

  onTouchEnd(): void {
    this.cleanupEventListeners();

    if (this.isTouchDragging) {
      this.isTouchDragging = false;
      const container = this.continentsContainer.nativeElement;
      this.renderer.removeClass(container, 'dragging');

      if (Math.abs(this.velocity) > 1) {
        this.applyInertia(this.velocity * 0.9);
      }
    }

    this.isPotentialDrag = false;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
  onContinentClick(event: MouseEvent | TouchEvent, i: number, id: number, title?: string): void {
    this.countries = [];
    const isTouchEvent = event.type === 'touchstart' || event.type === 'touchend';
    if (isTouchEvent && this.clickPrevented) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!isTouchEvent) {
      if (this.clickPrevented) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (Date.now() - this.dragStartTime < 300) {
        const container = this.continentsContainer.nativeElement;
        const currentX = (event as MouseEvent).pageX - container.offsetLeft;
        const distanceMoved = Math.abs(currentX - this.startX);
        if (distanceMoved > this.dragThreshold) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }
    }
    if (this.continentId === id) {
      this.continentId = null;
      this.emitContinents.emit(null);
    } else {
      this.continentId = id;
      this.emitContinents.emit(id.toString())
    }
    this.setContinentFilter(i, id, title);
  }

  onWheel(event: WheelEvent): void {
    const container = this.continentsContainer.nativeElement;
    if (container.scrollWidth <= container.clientWidth) {
      return;
    }
    event.preventDefault();
    if (this.wheelTimeout) {
      clearTimeout(this.wheelTimeout);
    }
    container.scrollLeft += event.deltaY * 0.5;
    this.updateScrollIndicators();
    this.wheelTimeout = setTimeout(() => {
    }, 150);
  }

  private applyInertia(velocity: number): void {
    if (Math.abs(velocity) < 0.5) return;
    const container = this.continentsContainer.nativeElement;
    const animate = () => {
      if (Math.abs(velocity) < 0.5) {
        if (this.inertiaAnimationId) {
          cancelAnimationFrame(this.inertiaAnimationId);
          this.inertiaAnimationId = null;
        }
        return;
      }
      container.scrollLeft += velocity;
      velocity *= 0.95;
      this.updateScrollIndicators();
      if (container.scrollLeft <= 0) {
        container.scrollLeft = 0;
        if (this.inertiaAnimationId) {
          cancelAnimationFrame(this.inertiaAnimationId);
          this.inertiaAnimationId = null;
        }
        return;
      }
      if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
        container.scrollLeft = container.scrollWidth - container.clientWidth;
        if (this.inertiaAnimationId) {
          cancelAnimationFrame(this.inertiaAnimationId);
          this.inertiaAnimationId = null;
        }
        return;
      }

      this.inertiaAnimationId = requestAnimationFrame(animate);
    };
    this.inertiaAnimationId = requestAnimationFrame(animate);
  }

  private updateScrollIndicators(): void {
    if (!this.continentsContainer) return;
    const container = this.continentsContainer.nativeElement;
    this.canScrollLeft = container.scrollLeft > 0;
    this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 1);
  }

  private reinitObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.scrollTrigger && !this.fullData) {
      this.setupIntersectionObserver();
    }
  }

  private setupIntersectionObserver() {
    if (this.fullData) {
      return;
    }

    if (!this.scrollTrigger) {
      this.retryCount++;
      if (this.retryCount <= this.maxRetries) {
        setTimeout(() => this.setupIntersectionObserver(), 200);
      }
      return;
    }

    this.retryCount = 0;

    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.01
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting &&
          !this.isLoading() &&
          !this.fullData &&
          this.countries.length > 0) {
          this.getMore();
        }
      });
    }, options);

    this.observer.observe(this.scrollTrigger.nativeElement);
  }

  findPosition(continents: LocationDto[], filter: LocationDto): number {
    const index = continents.findIndex(item => item.id === filter.id);
    return index !== -1 ? index + 1 : -1;
  }

  getList(param: string, id?: number, title?: string) {
    switch (param) {
      case 'continents': {
        this.loadingContinents = true;
        this.continentService.getContinentList(this.params)
          .pipe(takeUntil(this.destroy$))
          .subscribe((data) => {
            this.pagination = data;
            const content = data.content ?? [];
            const antarcticaIndex = content.findIndex(item => item.url === 'antarctica');
            if (antarcticaIndex > -1) {
              this.continents = [
                content[antarcticaIndex],
                ...content.slice(0, antarcticaIndex),
                ...content.slice(antarcticaIndex + 1)
              ];
            } else {
              this.continents = content;
            }
            this.loadingContinents = false;
            this.firstLoading.emit(false);
            setTimeout(() => this.updateScrollIndicators(), 100);
          });
        break;
      }
      case 'countries': {
        this.loadingCountries = true;
        switch (this.type) {
          case 'classic': {
            this.countryService.getCountryList(this.params)
              .pipe(takeUntil(this.destroy$))
              .subscribe((data) => {
                this.pagination = data;
                const totalElements = data.page?.totalElements || 0;
                const currentCount = data.content?.length || 0;
                this.fullData = currentCount >= totalElements;
                this.countries = data.content?.filter((v) => v.displayPage === true) ?? [];
                this.loadingCountries = false;
                setTimeout(() => this.reinitObserver(), 50);
                this.firstLoading.emit(false);
              });
            break;
          }
          case 'adventure': {
            if (this.adventureId !== null) {
              this.countryService.getCountryListByAdventureId(this.adventuresParams, this.adventureId)
                .pipe(takeUntil(this.destroy$))
                .subscribe((data) => {
                  this.pagination = data;
                  const totalElements = data.page?.totalElements || 0;
                  const currentCount = data.content?.length || 0;
                  this.fullData = currentCount >= totalElements;
                  this.countries = data.content?.filter((v) => v.displayPage === true) ?? [];
                  this.filterContinentsByCountries();
                  this.shuffleCountries();
                  this.loadingCountries = false;
                  setTimeout(() => this.reinitObserver(), 50);
                  this.firstLoading.emit(false);
                });
            } else {
              this.loadingCountries = false;
              this.firstLoading.emit(false);
            }
            break;
          }
        }
        break;
      }
      case 'continent': {
        this.continentFilterDestroy$.next();
        this.loadingContinent = true;
        if (this.type === 'classic') {
          if (id && id != 9) {
            this.countryService.getCountryListByContinent(this.params, id)
              .pipe(takeUntil(this.continentFilterDestroy$))
              .subscribe((data) => {
                this.pagination = data;
                const totalElements = data.page?.totalElements || 0;
                const currentCount = data.content?.length || 0;
                this.fullData = currentCount >= totalElements;
                this.countries = data.content?.filter((v) => v.displayPage === true) ?? [];
                this.loadingContinent = false;
                setTimeout(() => this.reinitObserver(), 50);
                this.firstLoading.emit(false);
              });
          } else {
            let routeCount = 0;
            this.routeService.getRouteList(this.antParams)
              .pipe(takeUntil(this.continentFilterDestroy$))
              .subscribe((data: Pagination<LocationDto>) => {
                const antRoutes = data.content ?? [];
                routeCount = antRoutes.length;
                this.countries = this.continents.filter((v) => v.id === id)
                this.countries[0].routeCount = routeCount;
                this.countries[0].cityCount = 0;
                this.countries[0].hotelCount = 0;
                this.loadingContinent = false;
                setTimeout(() => this.reinitObserver(), 50);
                this.firstLoading.emit(false);
              })
          }
          break;
        } else {
          if (this.adventureId !== null && id) {
            const params = new PaginationParams({
              sort: 'titleEn:ASC',
              query: '',
              pageSize: 200,
              pageNumber: 0,
              continentId: id.toString(),
            });
            this.countryService.getCountryListByAdventureId(params, this.adventureId)
              .pipe(takeUntil(this.continentFilterDestroy$))
              .subscribe((data) => {
                this.pagination = data;
                const totalElements = data.page?.totalElements || 0;
                const currentCount = data.content?.length || 0;
                this.fullData = currentCount >= totalElements;
                this.countries = data.content?.filter((v) => v.displayPage === true) ?? [];
                this.shuffleCountries();
                this.loadingContinent = false;
                setTimeout(() => this.reinitObserver(), 50);
                this.firstLoading.emit(false);
              });
          } else {
            this.loadingContinent = false;
            this.firstLoading.emit(false);
          }
          break;
        }
      }
    }
  }

  setContinentFilter(i: number, id: number, title?: string): any {
    this.loadingCountries = false;
    this.loadingContinent = false;

    this.params = new PaginationParams(this.defaultParams);
    if (i == this.activeContinent) {
      this.activeContinent = null;
      this.continentId = null;
      this.getList('countries');
    } else {
      this.activeContinent = i;
      this.continentId = id;
      this.getList('continent', id, title);
    }
  }

  getMore() {
    if (this.isScrollLoading || this.type === 'adventure') {
      return;
    }

    this.isScrollLoading = true;
    this.params.pageSize = this.params.pageSize + 10;

    if (this.alone) {
      if (this.loadContinent != undefined) {
        this.countryService.getCountryListByContinent(this.params, this.loadContinent)
          .pipe(takeUntil(this.destroy$))
          .subscribe((data) => {
            this.handleScrollResponse(data);
          });
      }
    } else {
      if (this.activeContinent != null && this.continentId != null) {
        this.countryService.getCountryListByContinent(this.params, this.continentId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((data) => {
            this.handleScrollResponse(data);
          });
      } else {
        this.countryService.getCountryList(this.params)
          .pipe(takeUntil(this.destroy$))
          .subscribe((data) => {
            this.handleScrollResponse(data);
          });
      }
    }
  }

  private handleScrollResponse(data: Pagination<LocationDto>) {
    this.pagination = data;
    const totalElements = data.page?.totalElements || 0;
    const currentCount = data.content?.length || 0;
    this.fullData = currentCount >= totalElements;

    const newCountries = data.content?.filter((v) => v.displayPage === true) ?? [];
    const existingIds = new Set(this.countries.map(country => country.id));
    const uniqueNewCountries = newCountries.filter(country => !existingIds.has(country.id));

    this.countries = [...this.countries, ...uniqueNewCountries];

    this.isScrollLoading = false;
    this.loadingCountries = false;

    setTimeout(() => this.reinitObserver(), 50);
  }

  hideMore() {
    this.params = new PaginationParams(this.defaultParams);
    if (this.activeContinent != null && this.continentId != null) {
      this.getList('continent', this.continentId);
      scrollUp();
    } else {
      this.getList('countries');
      scrollUp();
    }
  }

  filterContinentsByCountries(): void {
    const continentUrlsInCountries = new Set(
      this.countries
        .map(country => country.continent?.url)
        .filter(url => url != null)
    );

    this.filteredContinents = this.continents.filter(continent =>
      continentUrlsInCountries.has(continent.url)
    );
  }

  getContinents() {
    if (this.type === 'classic') {
      return this.continents;
    } else {
      return this.filteredContinents;
    }
  }

  shuffleCountries(): void {
    const array = [...this.countries];
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    this.countries = array.slice(0, 10);
  }

  protected readonly Icons = Icons;
}
